import instance from './instances.json'

/**
 * These are the currently supported versions of the Web SDK
 */
const currentBetaReleases = []
const currentStableReleases = {
  '26.3.x': ['26.3.0'],
  '26.2.x': ['26.2.0'],
  '26.1.x': ['26.1.0'],
  '25.4.x': ['25.4.0', '25.4.1', '25.4.2'],
}

/**
 * These are the versions of the SDK that will be available in the version selector in the customer facing Atomic Connect app
 */
export const ConnectAppSDKVersions = [
  {identifier: '26.3.0', name: 'latest stable (26.3.0)'},
  {identifier: '26.2.0', name: 'stable release (26.2.0)'},
  {identifier: '26.1.0', name: 'stable release (26.1.0)'},
  {identifier: '25.4.2', name: 'stable release (25.4.2)'},
]

/**
 * These are the versions of the SDK that will be available in the version selector in the internal Shell app
 */
export const ShellAppSDKVersions = [...ConnectAppSDKVersions]

/**
 * These are the versions of the SDK that will be available in the version selector in the internal Atomic Alpha app
 */
export const AlphaAppSDKVersions = [...ShellAppSDKVersions]

const featureFlags = {
  sdkEventObserver: {
    develop: new Set([
      ...currentStableReleases['26.3.x'],
      ...currentStableReleases['26.2.x'],
      ...currentStableReleases['26.1.x'],
      ...currentStableReleases['25.4.x'],
      ...currentBetaReleases,
    ]),
    staging: new Set([
      ...currentStableReleases['26.3.x'],
      ...currentStableReleases['26.2.x'],
      ...currentStableReleases['26.1.x'],
      ...currentStableReleases['25.4.x'],
      ...currentBetaReleases,
    ]),
    release: new Set([]),
  },
  headlessStreamContainer: {
    develop: new Set([
      ...currentStableReleases['26.3.x'],
      ...currentStableReleases['26.2.x'],
      ...currentStableReleases['26.1.x'],
      ...currentStableReleases['25.4.x'],
      ...currentBetaReleases,
    ]),
    staging: new Set([
      ...currentStableReleases['26.3.x'],
      ...currentStableReleases['26.2.x'],
      ...currentStableReleases['26.1.x'],
      ...currentStableReleases['25.4.x'],
      ...currentBetaReleases,
    ]),
    release: new Set([]),
  },
  fetchIconsRemotely: {
    develop: new Set([
      ...currentStableReleases['26.3.x'],
      ...currentStableReleases['26.2.x'],
      ...currentStableReleases['26.1.x'],
      ...currentStableReleases['25.4.x'],
      ...currentBetaReleases,
    ]),
    staging: new Set([
      ...currentStableReleases['26.3.x'],
      ...currentStableReleases['26.2.x'],
      ...currentStableReleases['26.1.x'],
      ...currentStableReleases['25.4.x'],
      ...currentBetaReleases,
    ]),
    release: new Set([]),
  },
  customTokenUserId: {
    develop: new Set([
      ...currentStableReleases['26.3.x'],
      ...currentStableReleases['26.2.x'],
      ...currentStableReleases['26.1.x'],
      ...currentStableReleases['25.4.x'],
      ...currentBetaReleases,
    ]),
    staging: new Set([
      ...currentStableReleases['26.3.x'],
      ...currentStableReleases['26.2.x'],
      ...currentStableReleases['26.1.x'],
      ...currentStableReleases['25.4.x'],
      ...currentBetaReleases,
    ]),
  },
  modalStreamContainer: {
    develop: new Set([
      ...currentStableReleases['26.3.x'],
      ...currentStableReleases['26.2.x'],
      ...currentStableReleases['26.1.x'],
      ...currentStableReleases['25.4.x'],
    ]),
    staging: new Set([
      ...currentStableReleases['26.3.x'],
      ...currentStableReleases['26.2.x'],
      ...currentStableReleases['26.1.x'],
      ...currentStableReleases['25.4.x'],
    ]),
    release: new Set([
      ...currentStableReleases['26.3.x'],
      ...currentStableReleases['26.2.x'],
      ...currentStableReleases['26.1.x'],
      ...currentStableReleases['25.4.x'],
    ]),
  },
  // `flushEvents`, `setEventFlushInterval` and `setApiProtocol` controls, SDK 26.3.0+
  analyticsEventFlush: {
    develop: new Set([...currentStableReleases['26.3.x']]),
    staging: new Set([...currentStableReleases['26.3.x']]),
    release: new Set([]),
  },
}

/**
 * SDK types that permit feature flags to be bypassed via the
 * `bypassFeatureFlags` query parameter.
 */
const bypassableSDKTypes = new Set(['develop', 'staging'])

/**
 * Feature flags are bypassed only while both `bypassFeatureFlags` and
 * `sdkVersion` are present in the current URL, and only on non-release
 * instances. Nothing is persisted.
 * @returns A boolean indicating whether feature flags are currently bypassed.
 */
export const isFeatureFlagBypassActive = () => {
  if (!bypassableSDKTypes.has(instance.sdkType)) {
    return false
  }
  if (!window.location || !window.location.search) {
    return false
  }

  const params = new URLSearchParams(window.location.search)
  return params.has('bypassFeatureFlags') && Boolean(params.get('sdkVersion'))
}

/**
 * @param {string} featureKey
 * @param {string} versionString
 * @returns A boolean indicating whether the feature is enabled for the given version.
 */
export const hasFeature = (featureKey, versionString) => {
  if (typeof featureKey !== 'string' && typeof versionString !== 'string') {
    return false
  }
  if (!featureFlags[featureKey]) {
    return false
  }
  if (isFeatureFlagBypassActive()) {
    return true
  }
  if (!featureFlags[featureKey][instance.sdkType]) {
    return false
  }
  if (!featureFlags[featureKey][instance.sdkType].has(versionString)) {
    return false
  }
  return true
}

/**
 * @param {string} versionString
 * @returns An array of feature keys that are excluded for the given version.
 */
export const getExcludedFeaturesList = versionString => {
  const excludedFeatures = []
  if (typeof versionString !== 'string') {
    return excludedFeatures
  }
  for (const featureKey in featureFlags) {
    if (!hasFeature(featureKey, versionString)) {
      excludedFeatures.push(featureKey)
    }
  }

  return excludedFeatures
}
